import { OverlayModule } from '@angular/cdk/overlay';
import { APP_BASE_HREF, PlatformLocation, registerLocaleData } from '@angular/common';
import localeEnCa from '@angular/common/locales/en-CA';
import localeFrCa from '@angular/common/locales/fr-CA';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import {
  ErrorHandler,
  enableProdMode,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';

import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';

import { firstValueFrom } from 'rxjs';

import { TokenService } from '@ph-core/services/auth/token.service';
import { ConfigService } from '@ph-core/services/config/config.service';
import { initDatadogRum } from '@ph-core/services/datadog/datadog';
import { GlobalErrorHandler } from '@ph-core/services/error/global-error.handler';
import { requestInterceptor } from '@ph-core/services/request-interceptor.service';
import { TranslationLoader } from '@ph-core/translate-loader/translate.loader';
import { CONTRACT_HISTORY_CONFIGURATION } from '@ph-shared/components/contract-history/models/contract-history-configuration.model';

import { APP_ROUTES } from './app/app-routes';
import { AppComponent } from './app/app.component';
import { SupportedLanguageCodes } from './app/static-data/languages';
import { versionSync } from './version-sync';

registerLocaleData(localeEnCa);
registerLocaleData(localeFrCa);

const HttpLoaderFactory = (): TranslationLoader => new TranslationLoader();

const environment = ConfigService.getEnvironment();

if (environment.production) {
  enableProdMode();
} else {
  if (environment?.apiMock?.enabled) {
    // eslint-disable-next-line
    const { worker } = require('./mocks/browser');
    worker.start({
      onUnhandledRequest: environment?.apiMock?.showWarnings ? 'warn' : 'bypass',
    });
  }
}

void bootstrapApplication(AppComponent, {
  providers: [
    provideAppInitializer(() => {
      versionSync(() => {
        const tokenService = inject(TokenService);
        tokenService.deleteToken();
        tokenService.deleteOriginalUserToken();
      });
    }),
    provideAppInitializer(() => {
      initDatadogRum(environment);
    }),
    provideRouter(APP_ROUTES),
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
        },
      }),
      OverlayModule
    ),
    provideAppInitializer(async () => {
      const translate = inject(TranslateService);
      const raw = localStorage.getItem('languageCode');
      const preferred =
        raw && Object.values(SupportedLanguageCodes).includes(raw as SupportedLanguageCodes)
          ? (raw as SupportedLanguageCodes)
          : SupportedLanguageCodes.EnglishCA;

      const tryUse = async (code: SupportedLanguageCodes): Promise<boolean> => {
        try {
          await firstValueFrom(translate.use(code));
          return true;
        } catch {
          return false;
        }
      };

      if (!(await tryUse(preferred)) && preferred !== SupportedLanguageCodes.EnglishCA) {
        await tryUse(SupportedLanguageCodes.EnglishCA);
      }
    }),
    {
      provide: APP_BASE_HREF,
      useFactory: (s: PlatformLocation): string => s.getBaseHrefFromDOM(),
      deps: [PlatformLocation],
    },
    {
      provide: CONTRACT_HISTORY_CONFIGURATION,
      useValue: { apiRoot: environment.apiUrl },
    },
    provideZonelessChangeDetection(),
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    provideBrowserGlobalErrorListeners(),
    provideAnimations(),
    provideHttpClient(withInterceptors([requestInterceptor]), withInterceptorsFromDi()),
  ],
});
